import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, finalize} from 'rxjs/operators';
import {LoadingBarService} from '@ngx-loading-bar/core';
import {AuthStore} from "../../../../home-layout/common/store/auth-store.service";
import {LibSnackbarService} from "../../../../../tal-library/snackbar/lib-snackbar/common/service/lib-snackbar.service";
import {
    LibSnackBarConfig,
    SNACK_BAR_TYPE
} from "../../../../../tal-library/snackbar/lib-snackbar/common/model/lib-snackbar.model";
import {environment} from "../../../../../../environments/environment";
import {ROUTE} from "../../../../home-layout/common/constant/routing.constant";
import {SilentErrorUtil} from "../../../../home-layout/common/util/silent-error.util";
import {
    HTTP_ERROR_MESSAGE,
    HTTP_ERROR_MESSAGE_BY_STATUS,
    HttpContextConstant
} from "../../../../home-layout/common/constant/http-context.constant";

@Injectable()
export class RequestInterceptor implements HttpInterceptor {

    private tokenizedRequest: HttpRequest<any>;

    constructor(private authStore: AuthStore,
                private libSnackbarService: LibSnackbarService,
                private loadingBarService: LoadingBarService) {
    }

    intercept(request: HttpRequest<any>, httpHandler: HttpHandler): Observable<HttpEvent<any>> {
        this.loadingBarService.useRef().start();
        this.tokenizedRequest = this.attachAnalyticsAuthorization(request);

        return this.handleRequest(httpHandler).pipe(catchError((error: any) => {
            if (SilentErrorUtil.isSilentError(error)) {
                return throwError(error);
            }
            (error?.error?.errorCode?.displayText)
                ? this.showDisplayTextMessage(error, request)
                : this.handleError(error, request);
            return throwError(error);
        }));
    }

    private attachAnalyticsAuthorization(httpRequest: HttpRequest<any>): HttpRequest<any> {
        if (!httpRequest.url?.startsWith(environment.analyticsApiUrl)) {
            return httpRequest.clone();
        }
        const accessToken: string = localStorage.getItem(ROUTE.TOKEN);
        if (!accessToken) {
            return httpRequest.clone();
        }
        return httpRequest.clone({setHeaders: {[ROUTE.AUTHORIZATION]: accessToken}});
    }

    private handleRequest(httpHandler: HttpHandler): Observable<HttpEvent<any>> {
        return httpHandler.handle(this.tokenizedRequest).pipe(
            finalize(() => {
                this.loadingBarService.useRef().stop();
            }));
    }

    private showDisplayTextMessage(httpErrorResponse: HttpErrorResponse, request: HttpRequest<unknown>): void {
        if (this.isErrorSnackbarSkipped(request)) {
            return;
        }

        this.libSnackbarService.openSnackBar(new LibSnackBarConfig(httpErrorResponse.error.errorCode.displayText, SNACK_BAR_TYPE.DANGER));
    }

    private handleError(error: any, request: HttpRequest<unknown>): void {
        if (error.status === HttpStatusCode.Forbidden && !this.isChatServiceRequest(request)) {
            this.authStore.logout();
        }
        if (this.isErrorSnackbarSkipped(request)) {
            return;
        }

        this.showErrorMessage(error, request);
    }

    private showErrorMessage(error: any, request: HttpRequest<unknown>): void {
        const message: string = this.getChatServiceErrorMessage(error, request) || this.getGenericErrorMessage(error);
        this.libSnackbarService.openSnackBar(new LibSnackBarConfig(message, SNACK_BAR_TYPE.DANGER));
    }

    private isChatServiceRequest(request: HttpRequest<unknown>): boolean {
        return request?.url?.startsWith(environment.chatApiUrl);
    }

    private isErrorSnackbarSkipped(request: HttpRequest<unknown>): boolean {
        return request?.context?.get(HttpContextConstant.SKIP_ERROR_SNACKBAR);
    }

    private getChatServiceErrorMessage(httpErrorResponse: HttpErrorResponse, request: HttpRequest<unknown>): string | null {
        if (!this.isChatServiceRequest(request) || !this.isClientError(httpErrorResponse)) {
            return null;
        }

        return httpErrorResponse?.error?.message ?? null;
    }

    private isClientError(httpErrorResponse: HttpErrorResponse): boolean {
        return httpErrorResponse?.status >= HttpStatusCode.BadRequest
            && httpErrorResponse?.status < HttpStatusCode.InternalServerError;
    }

    private getGenericErrorMessage(httpErrorResponse: HttpErrorResponse): string {
        return HTTP_ERROR_MESSAGE_BY_STATUS.get(httpErrorResponse?.status) ?? HTTP_ERROR_MESSAGE.GENERIC;
    }
}
